/**
 * DarkSkyCalendarWidget.ts
 *
 * Detailed forecast modal widget rendering a monthly stargazing calendar
 * with daily max darkness ratings (1.0-10.0), pitch-black dark hours count,
 * a continuous 744-hour diurnal curve canvas, and interactive celestial ephemeris.
 */

import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import * as SunCalc from 'suncalc';
import { getMoonPhaseDetails } from '../../../../utils/astronomy';

interface DailyMetrics {
  date: Date;
  day: number;
  maxScore: number;
  darkHours: number;
  darkStartMs: number | null;
  darkEndMs: number | null;
  hourlyScores: number[];
  phase: string;
  icon: string;
  illumination: string;
  fraction: number;
  sunrise: string;
  sunset: string;
  goldenMorn: string;
  goldenEve: string;
  blueEve: string;
  moonrise: string;
  moonset: string;
}

export class DarkSkyCalendarWidget implements WidgetRenderer {
  private rad = Math.PI / 180;
  private deg = 180 / Math.PI;

  private currentYear: number;
  private currentMonth: number;
  private selectedDay: number;
  private selectedHour: number = 22; // Default to 10 PM
  private metricsCache = new Map<string, DailyMetrics>();

  constructor() {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = now.getMonth();
    this.selectedDay = now.getDate();
  }

  // --- Celestial & Dark Skies Mathematics ---

  private toDays(date: Date): number {
    return date.getTime() / 86400000 + 2440587.5 - 2451545.0;
  }

  private gmst(d: number): number {
    return ((280.46061837 + 360.98564736629 * d) % 360 + 360) % 360;
  }

  private sunCoords(d: number): { ra: number; dec: number } {
    const M = ((357.5291 + 0.98560028 * d) % 360 + 360) % 360 * this.rad;
    const L = ((280.4665 + 0.98564736 * d) % 360 + 360) % 360 * this.rad;
    const lam = L + (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M)) * this.rad;
    const eps = (23.4393 - 0.0000004 * d) * this.rad;
    const sinDec = Math.sin(eps) * Math.sin(lam);
    const dec = Math.asin(Math.max(-1, Math.min(1, sinDec)));
    const ra = (Math.atan2(Math.cos(eps) * Math.sin(lam), Math.cos(lam)) * this.deg + 360) % 360;
    return { ra, dec };
  }

  private moonCoords(d: number, eps: number): { ra: number; dec: number } {
    const Lm = ((218.3165 + 13.17639648 * d) % 360 + 360) % 360 * this.rad;
    const Mm = ((134.9634 + 13.06499295 * d) % 360 + 360) % 360 * this.rad;
    const F = ((93.2721 + 13.22935024 * d) % 360 + 360) % 360 * this.rad;
    const D = ((350.7377 + 12.19074919 * d) % 360 + 360) % 360 * this.rad;

    const lamM = Lm + (6.289 * Math.sin(Mm) - 1.274 * Math.sin(Mm - 2 * D) + 0.658 * Math.sin(2 * D) - 0.214 * Math.sin(2 * Mm)) * this.rad;
    const betaM = (5.128 * Math.sin(F)) * this.rad;

    const sinDec = Math.sin(betaM) * Math.cos(eps) + Math.cos(betaM) * Math.sin(eps) * Math.sin(lamM);
    const dec = Math.asin(Math.max(-1, Math.min(1, sinDec)));
    const y = Math.cos(betaM) * Math.cos(eps) * Math.sin(lamM) - Math.sin(betaM) * Math.sin(eps);
    const x = Math.cos(betaM) * Math.cos(lamM);
    const ra = (Math.atan2(y, x) * this.deg + 360) % 360;
    return { ra, dec };
  }

  private altitude(latDeg: number, lonDeg: number, gmstDeg: number, raDeg: number, decRad: number): number {
    const latRad = latDeg * this.rad;
    const lha = ((gmstDeg + lonDeg - raDeg) % 360 + 360) % 360 * this.rad;
    const sinAlt = Math.sin(latRad) * Math.sin(decRad) + Math.cos(latRad) * Math.cos(decRad) * Math.cos(lha);
    return Math.asin(Math.max(-1, Math.min(1, sinAlt))) * this.deg;
  }

  private calculateScore(sunAlt: number, moonAlt: number, moonFraction: number, bortle: number): number {
    const baseScore = Math.max(1.0, Math.min(10.0, 10.0 - (bortle - 1.0) * 1.1));
    let moonPenalty = 0;
    if (moonAlt > 0) {
      moonPenalty = moonFraction * Math.sqrt(Math.sin(moonAlt * this.rad)) * 7.0;
    }
    let sunFactor = 0;
    if (sunAlt <= -18.0) {
      sunFactor = 1.0;
    } else if (sunAlt < -6.0) {
      sunFactor = Math.pow((sunAlt - (-6.0)) / (-18.0 - (-6.0)), 4);
    }
    return Math.max(0.0, Math.min(10.0, (baseScore - moonPenalty) * sunFactor));
  }

  private getDarkSkiesRgb(score: number): { red: number; green: number; blue: number } {
    let r = 0, g = 0, b = 0;
    if (score <= 2.0) {
      const f = Math.max(0, score / 2.0);
      r = Math.round(180 + (255 - 180) * f);
      g = Math.round(128 * f);
      b = 0;
    } else if (score <= 4.0) {
      const f = (score - 2.0) / 2.0;
      r = 255;
      g = Math.round(128 + 127 * f);
      b = 0;
    } else if (score <= 6.0) {
      const f = (score - 4.0) / 2.0;
      r = Math.round(255 * (1 - f));
      g = 255;
      b = 0;
    } else if (score <= 8.0) {
      const f = (score - 6.0) / 2.0;
      r = 0;
      g = 255;
      b = Math.round(255 * f);
    } else {
      const f = Math.min(1, (score - 8.0) / 2.0);
      r = 0;
      g = Math.round(255 * (1 - f * 0.80));
      b = 255;
    }
    return { red: r, green: g, blue: b };
  }

  private getDarkSkiesHex(score: number): string {
    const { red, green, blue } = this.getDarkSkiesRgb(score);
    return `rgb(${red},${green},${blue})`;
  }

  private formatTime(d: Date | null | undefined): string {
    if (!d || isNaN(d.getTime())) return '--:--';
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  private getDailyMetrics(year: number, month: number, day: number, lat: number, lon: number, bortle: number): DailyMetrics {
    const key = `${year}-${month}-${day}-${lat.toFixed(2)}-${lon.toFixed(2)}-${bortle}`;
    if (this.metricsCache.has(key)) {
      return this.metricsCache.get(key)!;
    }

    const noonDate = new Date(year, month, day, 12, 0, 0);
    const moon = getMoonPhaseDetails(noonDate);
    const noonIllum = SunCalc.getMoonIllumination(noonDate);
    const sTimes = SunCalc.getTimes(noonDate, lat, lon);
    const mTimes = SunCalc.getMoonTimes(noonDate, lat, lon);

    let maxDarkScore = 0;
    let darkMinutes = 0;
    let darkStartMs: number | null = null;
    let darkEndMs: number | null = null;
    const hourlyScores: number[] = [];

    // 24-hour evaluation
    for (let h = 0; h < 24; h++) {
      const evalDate = new Date(year, month, day, h, 0, 0);
      const d = this.toDays(evalDate);
      const g = this.gmst(d);
      const sun = this.sunCoords(d);
      const eps = 23.4393 * this.rad;
      const moonC = this.moonCoords(d, eps);
      const moonIllum = SunCalc.getMoonIllumination(evalDate);
      const sAlt = this.altitude(lat, lon, g, sun.ra, sun.dec);
      const mAlt = this.altitude(lat, lon, g, moonC.ra, moonC.dec);
      const sc = this.calculateScore(sAlt, mAlt, moonIllum.fraction, bortle);
      hourlyScores.push(Math.round(sc * 10) / 10);
    }

    // High-resolution nocturnal sweep (5 PM to 8 AM next morning)
    const nightStartMs = new Date(year, month, day, 17, 0, 0).getTime();
    const nightEndMs = nightStartMs + 15 * 3600 * 1000;
    const stepIntervalMs = 15 * 60 * 1000;

    for (let tMs = nightStartMs; tMs <= nightEndMs; tMs += stepIntervalMs) {
      const tDate = new Date(tMs);
      const d = this.toDays(tDate);
      const g = this.gmst(d);
      const sun = this.sunCoords(d);
      const eps = 23.4393 * this.rad;
      const moonC = this.moonCoords(d, eps);
      const tMoonIllum = SunCalc.getMoonIllumination(tDate);
      const sAlt = this.altitude(lat, lon, g, sun.ra, sun.dec);
      const mAlt = this.altitude(lat, lon, g, moonC.ra, moonC.dec);
      const sc = this.calculateScore(sAlt, mAlt, tMoonIllum.fraction, bortle);

      if (sc > maxDarkScore) maxDarkScore = sc;

      const isSunDark = sAlt <= -18.0;
      const isMoonDark = mAlt <= 0.0 || tMoonIllum.fraction <= 0.03;
      if (isSunDark && isMoonDark) {
        darkMinutes += 15;
        if (darkStartMs === null) darkStartMs = tMs;
        darkEndMs = tMs;
      }
    }

    const darkHours = Math.round((darkMinutes / 60) * 10) / 10;

    const res: DailyMetrics = {
      date: noonDate,
      day,
      maxScore: Math.round(maxDarkScore * 10) / 10,
      darkHours,
      darkStartMs,
      darkEndMs,
      hourlyScores,
      phase: moon.name,
      icon: moon.icon,
      illumination: moon.illumination,
      fraction: noonIllum.fraction,
      sunrise: this.formatTime(sTimes.sunrise),
      sunset: this.formatTime(sTimes.sunset),
      goldenMorn: `${this.formatTime(sTimes.sunrise)} - ${this.formatTime(sTimes.goldenHourEnd)}`,
      goldenEve: `${this.formatTime(sTimes.goldenHour)} - ${this.formatTime(sTimes.sunset)}`,
      blueEve: this.formatTime(sTimes.dusk),
      moonrise: this.formatTime(mTimes.rise),
      moonset: this.formatTime(mTimes.set)
    };

    this.metricsCache.set(key, res);
    return res;
  }

  // --- HTML Assembly ---

  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Dark Sky & Ephemeris Calendar';
    const opts = widget.options || {};
    const showCurve = opts.showDiurnalCurve !== false;
    const showDarkScores = opts.showDarkScores !== false;
    const showDarkHours = opts.showDarkHours !== false;
    const showDetails = opts.showEphemerisDetails !== false;
    const bortle = (opts.bortleOverride && opts.bortleOverride !== 'auto') ? parseInt(opts.bortleOverride, 10) : 3;

    const lat = ctx.lat || 47.6;
    const lon = ctx.lon || -122.3;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const curMonthName = monthNames[this.currentMonth];
    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();

    const today = new Date();
    const isThisMonth = today.getFullYear() === this.currentYear && today.getMonth() === this.currentMonth;
    const todayDay = isThisMonth ? today.getDate() : -1;

    // 1. Calendar Grid Cells
    let cellsHtml = '';
    for (let p = 0; p < firstDay; p++) {
      cellsHtml += `<div style="background: transparent; min-height: 48px;"></div>`;
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const m = this.getDailyMetrics(this.currentYear, this.currentMonth, d, lat, lon, bortle);
      const isToday = d === todayDay;
      const isSelected = d === this.selectedDay;
      const scoreColor = this.getDarkSkiesHex(m.maxScore);
      const isMajor = m.phase.includes('New') || m.phase.includes('Full');

      cellsHtml += `
        <div 
          class="darksky-cal-cell ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}" 
          data-day="${d}"
          style="display: flex; flex-direction: column; align-items: center; justify-content: space-between; padding: 4px 2px; border-radius: 6px; background: ${isSelected ? 'rgba(56, 189, 248, 0.20)' : (isToday ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.55)')}; border: 1.5px solid ${isSelected ? '#38bdf8' : (isToday ? 'rgba(56, 189, 248, 0.5)' : (isMajor ? 'rgba(251, 191, 36, 0.35)' : 'rgba(255, 255, 255, 0.06)'))}; min-height: 48px; cursor: pointer; user-select: none; transition: all 0.15s ease;"
          title="${curMonthName} ${d}: Score ${m.maxScore}/10 (${m.darkHours} dark hrs, ${m.phase})"
        >
          <div style="display: flex; justify-content: space-between; width: 100%; padding: 0 3px; font-size: 0.65rem;">
            <span style="font-weight: ${isToday || isSelected ? '800' : '600'}; color: ${isSelected ? '#38bdf8' : (isToday ? '#f8fafc' : '#94a3b8')};">${d}</span>
            <span style="font-size: 0.70rem; line-height: 1;">${m.icon}</span>
          </div>

          ${showDarkScores ? `
            <div style="font-size: 0.62rem; font-weight: 800; color: ${scoreColor}; font-family: monospace; background: rgba(0,0,0,0.4); padding: 0 4px; border-radius: 3px; border: 1px solid ${scoreColor}40;">
              ${m.maxScore.toFixed(1)}
            </div>
          ` : ''}

          ${showDarkHours ? `
            <div style="font-size: 0.55rem; color: #94a3b8; font-weight: 600;">
              ${m.darkHours > 0 ? `🌑 ${m.darkHours.toFixed(1)}h` : '0h'}
            </div>
          ` : ''}
        </div>
      `;
    }

    // 2. Active Selected Day Details
    const activeM = this.getDailyMetrics(this.currentYear, this.currentMonth, this.selectedDay, lat, lon, bortle);
    const selScoreRgb = this.getDarkSkiesHex(activeM.maxScore);

    let pitchBlackStr = 'None (moon or sun present all night)';
    if (activeM.darkHours > 0 && activeM.darkStartMs && activeM.darkEndMs) {
      const sT = new Date(activeM.darkStartMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const eT = new Date(activeM.darkEndMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      pitchBlackStr = `${sT} – ${eT} (${activeM.darkHours.toFixed(1)} hrs)`;
    }

    const selDateStr = new Date(this.currentYear, this.currentMonth, this.selectedDay).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });

    return `
      <div id="widget-${widget.id}" class="metric-card darksky-calendar-widget" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 12px 14px; box-shadow: 0 8px 24px rgba(0,0,0,0.3); display: flex; flex-direction: column; gap: 8px;">
        
        <!-- Header & Month Controls -->
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 6px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-size: 0.95rem;">🌌</span>
            <span style="font-size: 0.78rem; font-weight: 700; color: #f8fafc; text-transform: uppercase; letter-spacing: 0.5px;">${title}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 4px;">
            <button type="button" class="btn-darksky-prev-month" style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #38bdf8; font-size: 0.65rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; cursor: pointer;">◀ Prev</button>
            <span class="darksky-month-label" style="font-size: 0.74rem; font-weight: 700; color: #fff; min-width: 90px; text-align: center;">${curMonthName} ${this.currentYear}</span>
            <button type="button" class="btn-darksky-next-month" style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #38bdf8; font-size: 0.65rem; font-weight: 700; padding: 2px 6px; border-radius: 4px; cursor: pointer;">Next ▶</button>
          </div>
        </div>

        <!-- Planning Disclaimer -->
        <div style="font-size: 0.62rem; color: #94a3b8; background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.2); padding: 4px 6px; border-radius: 4px; line-height: 1.3;">
          ℹ️ <span>Outlook based on static Bortle (${bortle}) and solar/lunar phases, no atmospheric data. Intended for long term planning.</span>
        </div>

        <!-- Calendar Days Grid -->
        <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; text-align: center;">
          <div style="font-size: 0.60rem; font-weight: 700; color: #64748b; padding: 2px 0;">SU</div>
          <div style="font-size: 0.60rem; font-weight: 700; color: #64748b; padding: 2px 0;">MO</div>
          <div style="font-size: 0.60rem; font-weight: 700; color: #64748b; padding: 2px 0;">TU</div>
          <div style="font-size: 0.60rem; font-weight: 700; color: #64748b; padding: 2px 0;">WE</div>
          <div style="font-size: 0.60rem; font-weight: 700; color: #64748b; padding: 2px 0;">TH</div>
          <div style="font-size: 0.60rem; font-weight: 700; color: #64748b; padding: 2px 0;">FR</div>
          <div style="font-size: 0.60rem; font-weight: 700; color: #64748b; padding: 2px 0;">SA</div>
          ${cellsHtml}
        </div>

        <!-- Continuous Month-Long Diurnal Chart -->
        ${showCurve ? `
          <div style="display: flex; flex-direction: column; gap: 3px; background: rgba(15, 23, 42, 0.7); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 8px; padding: 6px 8px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 0.66rem; font-weight: 700; color: #38bdf8; text-transform: uppercase;">📈 744-Hour Monthly Diurnal Darkness Wave</span>
              <span style="font-size: 0.60rem; color: #64748b;">Tap or scrub anywhere along month</span>
            </div>
            <div class="darksky-diurnal-container" style="width: 100%; height: 70px; position: relative;">
              <canvas id="canvas-darksky-curve-${widget.id}" style="width: 100%; height: 70px; display: block;"></canvas>
            </div>
          </div>
        ` : ''}

        <!-- Selected Date Ephemeris & Stargazing Outlook Card -->
        ${showDetails ? `
          <div class="darksky-selected-card" style="background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(99, 102, 241, 0.35); border-radius: 8px; padding: 8px 10px; display: flex; flex-direction: column; gap: 6px;">
            <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 4px;">
              <div style="font-size: 0.74rem; font-weight: 700; color: #f1f5f9;">
                🗓️ <span class="val-selected-date">${selDateStr}</span>
              </div>
              <div style="font-size: 0.70rem; font-weight: 800; color: ${selScoreRgb}; background: rgba(0,0,0,0.4); padding: 1px 6px; border-radius: 4px; border: 1px solid ${selScoreRgb}40;">
                <span class="val-selected-score">${activeM.maxScore.toFixed(1)}/10</span>
              </div>
            </div>

            <div style="font-size: 0.66rem; color: #cbd5e1; background: rgba(0,0,0,0.25); padding: 4px 6px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.04);">
              🌑 Prime Dark Window: <strong class="val-pitch-window" style="color: #38bdf8;">${pitchBlackStr}</strong>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 0.66rem; line-height: 1.35;">
              <div style="display: flex; flex-direction: column; gap: 2px;">
                <div style="color: #f97316; font-weight: 700;">🌅 Solar Times</div>
                <div>Rise: <strong class="val-sunrise">${activeM.sunrise}</strong></div>
                <div>Set: <strong class="val-sunset">${activeM.sunset}</strong></div>
                <div>Golden: <span class="val-golden" style="color: #fbbf24;">${activeM.goldenEve}</span></div>
              </div>
              <div style="display: flex; flex-direction: column; gap: 2px;">
                <div style="color: #38bdf8; font-weight: 700;">🌙 Lunar Times</div>
                <div>Phase: <strong class="val-moon-phase">${activeM.icon} ${activeM.phase}</strong> (${activeM.illumination})</div>
                <div>Rise: <strong class="val-moonrise">${activeM.moonrise}</strong></div>
                <div>Set: <strong class="val-moonset">${activeM.moonset}</strong></div>
              </div>
            </div>
          </div>
        ` : ''}

      </div>
    `;
  }

  // --- Dynamic Mounting & Canvas Drawing ---

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.getElementById(`widget-${widget.id}`);
    if (!container) return;

    const opts = widget.options || {};
    const bortle = (opts.bortleOverride && opts.bortleOverride !== 'auto') ? parseInt(opts.bortleOverride, 10) : 3;
    const lat = ctx.lat || 47.6;
    const lon = ctx.lon || -122.3;

    // Draw monthly diurnal curve
    this.drawMonthlyCurve(widget, lat, lon, bortle);

    // Bind Month Navigation
    const prevBtn = container.querySelector('.btn-darksky-prev-month');
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.currentMonth--;
        if (this.currentMonth < 0) {
          this.currentMonth = 11;
          this.currentYear--;
        }
        const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
        this.selectedDay = Math.min(this.selectedDay, daysInMonth);
        this.refreshWidget(widget, ctx);
      });
    }

    const nextBtn = container.querySelector('.btn-darksky-next-month');
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.currentMonth++;
        if (this.currentMonth > 11) {
          this.currentMonth = 0;
          this.currentYear++;
        }
        const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
        this.selectedDay = Math.min(this.selectedDay, daysInMonth);
        this.refreshWidget(widget, ctx);
      });
    }

    // Bind Day Cell Clicks
    container.querySelectorAll('.darksky-cal-cell').forEach(cell => {
      cell.addEventListener('click', (e) => {
        e.stopPropagation();
        const day = parseInt((cell as HTMLElement).dataset.day || '1', 10);
        this.selectedDay = day;
        this.updateSelectedDetails(container, widget, lat, lon, bortle);
        this.drawMonthlyCurve(widget, lat, lon, bortle);
      });
    });

    // Bind Diurnal Canvas Scrubbing
    const canvas = container.querySelector(`#canvas-darksky-curve-${widget.id}`) as HTMLCanvasElement;
    if (canvas) {
      const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
      const totalHours = daysInMonth * 24;

      const handleScrub = (e: MouseEvent | TouchEvent) => {
        const rect = canvas.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clickX = clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / rect.width));
        const totalH = Math.min(totalHours - 1, Math.floor(ratio * totalHours));
        this.selectedDay = Math.floor(totalH / 24) + 1;
        this.selectedHour = totalH % 24;
        this.updateSelectedDetails(container, widget, lat, lon, bortle);
        this.drawMonthlyCurve(widget, lat, lon, bortle);
      };

      canvas.addEventListener('click', handleScrub);
    }
  }

  private refreshWidget(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.getElementById(`widget-${widget.id}`);
    if (!container) return;
    const parent = container.parentElement;
    if (!parent) return;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.renderHtml(widget, ctx);
    const newEl = tempDiv.firstElementChild as HTMLElement;
    if (newEl) {
      parent.replaceChild(newEl, container);
      this.init(widget, ctx);
    }
  }

  private updateSelectedDetails(container: HTMLElement, _widget: ForecastWidgetConfig, lat: number, lon: number, bortle: number): void {
    const m = this.getDailyMetrics(this.currentYear, this.currentMonth, this.selectedDay, lat, lon, bortle);

    // Update active cell classes
    container.querySelectorAll('.darksky-cal-cell').forEach(c => {
      const d = parseInt((c as HTMLElement).dataset.day || '0', 10);
      if (d === this.selectedDay) {
        c.classList.add('selected');
        (c as HTMLElement).style.background = 'rgba(56, 189, 248, 0.20)';
        (c as HTMLElement).style.borderColor = '#38bdf8';
      } else {
        c.classList.remove('selected');
        (c as HTMLElement).style.background = 'rgba(15, 23, 42, 0.55)';
        (c as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.06)';
      }
    });

    const scoreColor = this.getDarkSkiesHex(m.maxScore);
    const selDateStr = new Date(this.currentYear, this.currentMonth, this.selectedDay).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });

    let pitchBlackStr = 'None (moon or sun present all night)';
    if (m.darkHours > 0 && m.darkStartMs && m.darkEndMs) {
      const sT = new Date(m.darkStartMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const eT = new Date(m.darkEndMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      pitchBlackStr = `${sT} – ${eT} (${m.darkHours.toFixed(1)} hrs)`;
    }

    const valDate = container.querySelector('.val-selected-date');
    if (valDate) valDate.textContent = selDateStr;

    const valScore = container.querySelector('.val-selected-score');
    if (valScore) {
      valScore.textContent = `${m.maxScore.toFixed(1)}/10`;
      (valScore.parentElement as HTMLElement).style.color = scoreColor;
      (valScore.parentElement as HTMLElement).style.borderColor = `${scoreColor}40`;
    }

    const valPitch = container.querySelector('.val-pitch-window');
    if (valPitch) valPitch.textContent = pitchBlackStr;

    const valSunrise = container.querySelector('.val-sunrise');
    if (valSunrise) valSunrise.textContent = m.sunrise;

    const valSunset = container.querySelector('.val-sunset');
    if (valSunset) valSunset.textContent = m.sunset;

    const valGolden = container.querySelector('.val-golden');
    if (valGolden) valGolden.textContent = m.goldenEve;

    const valMoonPhase = container.querySelector('.val-moon-phase');
    if (valMoonPhase) valMoonPhase.textContent = `${m.icon} ${m.phase}`;

    const valMoonrise = container.querySelector('.val-moonrise');
    if (valMoonrise) valMoonrise.textContent = m.moonrise;

    const valMoonset = container.querySelector('.val-moonset');
    if (valMoonset) valMoonset.textContent = m.moonset;
  }

  private drawMonthlyCurve(widget: ForecastWidgetConfig, lat: number, lon: number, bortle: number): void {
    const canvas = document.getElementById(`canvas-darksky-curve-${widget.id}`) as HTMLCanvasElement;
    if (!canvas) return;

    const container = canvas.parentElement;
    const width = container ? container.clientWidth : 400;
    const height = 70;

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width, h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    const totalHours = daysInMonth * 24;

    const monthScores: number[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const m = this.getDailyMetrics(this.currentYear, this.currentMonth, d, lat, lon, bortle);
      for (let hr = 0; hr < 24; hr++) {
        monthScores.push((m.hourlyScores && m.hourlyScores[hr] !== undefined) ? m.hourlyScores[hr] : 0);
      }
    }

    const topPad = 16;
    const botPad = 4;
    const plotH = h - topPad - botPad;
    const stepX = w / totalHours;

    // 1. Day separator lines & labels
    for (let d = 1; d <= daysInMonth; d++) {
      const dayLeft = (d - 1) * 24 * stepX;
      const dayWidth = 24 * stepX;

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(dayLeft, 0);
      ctx.lineTo(dayLeft, h);
      ctx.stroke();

      const isSel = (d === this.selectedDay);
      ctx.fillStyle = isSel ? '#38bdf8' : 'rgba(255, 255, 255, 0.65)';
      ctx.font = isSel ? 'bold 8px monospace' : '7px monospace';
      ctx.textAlign = 'center';
      if (d === 1 || d % 5 === 0 || d === daysInMonth || isSel) {
        ctx.fillText(String(d), dayLeft + dayWidth * 0.5, 9);
      }
    }

    // 2. Score 5.0 guide
    const y5 = h - botPad - 0.5 * plotH;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y5);
    ctx.lineTo(w, y5);
    ctx.stroke();

    // 3. Highlight selected day bracket
    const selLeft = (this.selectedDay - 1) * 24 * stepX;
    const selWidth = 24 * stepX;
    ctx.fillStyle = 'rgba(0, 50, 255, 0.15)';
    ctx.fillRect(selLeft, topPad, selWidth, plotH);
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.7)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(selLeft, topPad, selWidth, plotH);

    // 4. Continuous score curve
    ctx.beginPath();
    for (let i = 0; i < totalHours; i++) {
      const sc = monthScores[i] || 0;
      const x = i * stepX + stepX * 0.5;
      const y = h - botPad - (sc / 10.0) * plotH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 1.6;
    ctx.stroke();

    // 5. Area fill gradient
    ctx.lineTo(w, h - botPad);
    ctx.lineTo(0, h - botPad);
    ctx.closePath();

    const fillGrad = ctx.createLinearGradient(0, topPad, 0, h - botPad);
    fillGrad.addColorStop(0.0, 'rgba(0, 50, 255, 0.45)');
    fillGrad.addColorStop(0.3, 'rgba(0, 200, 200, 0.30)');
    fillGrad.addColorStop(0.6, 'rgba(34, 197, 94, 0.20)');
    fillGrad.addColorStop(0.85, 'rgba(245, 158, 11, 0.10)');
    fillGrad.addColorStop(1.0, 'rgba(180, 0, 0, 0.03)');
    ctx.fillStyle = fillGrad;
    ctx.fill();

    // 6. Selected hour needle
    const curTotalHour = (this.selectedDay - 1) * 24 + this.selectedHour;
    const needleX = curTotalHour * stepX + stepX * 0.5;
    const curScore = monthScores[curTotalHour] || 0;
    const needleY = h - botPad - (curScore / 10.0) * plotH;

    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(needleX, topPad);
    ctx.lineTo(needleX, h - botPad);
    ctx.stroke();

    ctx.fillStyle = '#f59e0b';
    ctx.beginPath();
    ctx.arc(needleX, needleY, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}
